import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatBRL, STATUS_STYLES, DOC_TYPES, DOC_STATUS } from "@/lib/constants";
import { Search, Plus, FileText, SlidersHorizontal, Eye, Pencil, Copy, Download, MessageCircle, PenLine, Loader2, Trash2, CheckSquare, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const fmtDate = (d) => d.date || (d.created_at ? new Date(d.created_at).toLocaleDateString("pt-BR") : "");

const DocActions = ({ doc, onClose, onChanged }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const remove = async () => {
    if (!confirm) return setConfirm(true);
    setBusy("del");
    try { await api.delete(`/documents/${doc.id}`); toast.success("Documento excluído"); onChanged(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); } finally { setBusy(""); }
  };
  const go = (to) => { onClose(); navigate(to); };
  const download = async () => {
    setBusy("pdf");
    try { const res = await fetch(`${API}/documents/${doc.id}/pdf`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, credentials: "include" }); const a = document.createElement("a"); a.href = URL.createObjectURL(await res.blob()); a.download = `${doc.number}.pdf`; a.click(); }
    catch (e) { toast.error("Não foi possível gerar o PDF"); } finally { setBusy(""); }
  };
  const duplicate = async () => {
    setBusy("dup");
    try { const { data } = await api.post(`/documents/${doc.id}/duplicate`); toast.success("Documento duplicado"); onChanged(); go(`/documents/${data.id}/edit`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao duplicar"); } finally { setBusy(""); }
  };
  const whatsapp = () => {
    const wa = (doc.client_whatsapp || doc.client_phone || "").replace(/\D/g, "");
    const num = wa && wa.length <= 11 ? `55${wa}` : wa;
    const link = `${window.location.origin}/doc/${doc.share_token}`;
    const msg = encodeURIComponent(`Olá ${doc.client_name}! Segue seu ${DOC_TYPES[doc.type]?.label} ${doc.number}.\nVisualize aqui: ${link}`);
    if (num) window.open(`https://wa.me/${num}?text=${msg}`, "_blank"); else { navigator.clipboard.writeText(link); toast.success("Link copiado!"); }
  };
  const A = ({ icon: Icon, label, onClick, testid, k }) => (
    <button onClick={onClick} disabled={!!busy} data-testid={testid} className="flex items-center gap-3 h-12 px-3 rounded-xl hover:bg-accent text-sm font-medium w-full text-left transition-colors disabled:opacity-50">
      {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4 text-muted-foreground" />}{label}
    </button>
  );
  return (
    <div className="space-y-0.5" data-testid="doc-actions">
      <A icon={Eye} label="Visualizar" onClick={() => go(`/documents/${doc.id}`)} testid="doc-action-view" />
      {doc.status !== "Assinado" && <A icon={Pencil} label="Editar" onClick={() => go(`/documents/${doc.id}/edit`)} testid="doc-action-edit" />}
      <A icon={Copy} label="Duplicar" onClick={duplicate} testid="doc-action-duplicate" k="dup" />
      <A icon={Download} label="Baixar PDF" onClick={download} testid="doc-action-pdf" k="pdf" />
      <A icon={MessageCircle} label="Enviar pelo WhatsApp" onClick={whatsapp} testid="doc-action-whatsapp" />
      {doc.status !== "Assinado" && <A icon={PenLine} label="Assinatura" onClick={() => go(`/documents/${doc.id}/sign`)} testid="doc-action-sign" />}
      {user?.role !== "tecnico" && (
        <button onClick={remove} disabled={!!busy} data-testid="doc-action-delete" className={`flex items-center gap-3 h-12 px-3 rounded-xl text-sm font-medium w-full text-left transition-colors mt-1 ${confirm ? "bg-destructive text-destructive-foreground" : "text-destructive hover:bg-destructive/10"}`}>
          {busy === "del" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}{confirm ? "Toque novamente para confirmar a exclusão" : "Excluir documento"}
        </button>
      )}
    </div>
  );
};

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");
  const [filters, setFilters] = useState(false);
  const [sel, setSel] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [checked, setChecked] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role !== "tecnico";
  const toggle = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const exitSelect = () => { setSelectMode(false); setChecked([]); setConfirmBulk(false); };
  const bulkDelete = async () => {
    setDeleting(true);
    try { const { data } = await api.post("/documents/bulk-delete", { ids: checked }); toast.success(`${data.deleted} documento(s) excluído(s)`); exitSelect(); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); } finally { setDeleting(false); }
  };

  const load = () => {
    const params = {};
    if (type !== "all") params.type = type;
    if (status !== "all") params.status = status;
    if (period !== "all") params.period = period;
    api.get("/documents", { params }).then(({ data }) => setDocs(data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [type, status, period]);

  const list = (docs || []).filter((d) => !q || `${d.number} ${d.client_name} ${d.equipment_name || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="documents-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Meus documentos</h1>
        <Button onClick={() => navigate("/documents/new")} className="rounded-xl" data-testid="docs-new-btn"><Plus className="h-4 w-4 mr-1" /> Criar documento</Button>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por cliente ou número" className="pl-10 h-11 rounded-xl" data-testid="docs-search-input" />
        </div>
        <Button variant={filters || type !== "all" || status !== "all" || period !== "all" ? "secondary" : "outline"} className="h-11 rounded-xl px-3" onClick={() => setFilters((v) => !v)} data-testid="docs-filters-toggle"><SlidersHorizontal className="h-4 w-4" /></Button>
        {canDelete && <Button variant={selectMode ? "secondary" : "outline"} className="h-11 rounded-xl px-3" onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="docs-select-toggle" aria-label="Selecionar vários">{selectMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}</Button>}
      </div>
      {selectMode && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm" data-testid="docs-bulk-bar">
          <button onClick={() => setChecked(checked.length === list.length ? [] : list.map((d) => d.id))} className="font-medium h-9 px-2 rounded-lg hover:bg-accent whitespace-nowrap text-xs sm:text-sm" data-testid="docs-select-all">{checked.length === list.length && list.length ? "Desmarcar todos" : "Selecionar todos"}</button>
          <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap" data-testid="docs-selected-count">{checked.length} selecionado(s)</span>
          <Button size="sm" variant="destructive" className="h-9 rounded-lg" disabled={!checked.length || deleting} onClick={() => setConfirmBulk(true)} data-testid="docs-bulk-delete"><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button>
        </div>
      )}
      {filters && (
        <div className="grid grid-cols-3 gap-2" data-testid="docs-filters">
          <Select value={type} onValueChange={setType}><SelectTrigger className="h-10" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos os tipos</SelectItem>{Object.entries(DOC_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.plural || v.label}</SelectItem>)}</SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-10" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Todos os status</SelectItem>{DOC_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          <Select value={period} onValueChange={setPeriod}><SelectTrigger className="h-10" data-testid="filter-period"><SelectValue /></SelectTrigger>
            <SelectContent>{[["all", "Qualquer data"], ["today", "Hoje"], ["week", "Esta semana"], ["month", "Este mês"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
        </div>
      )}

      {docs === null ? <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        : list.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" /><p className="text-sm">Nenhum documento encontrado.</p></div>
      ) : (
        <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="docs-list">
          <div className={`hidden sm:grid grid-cols-[1.2fr_1.4fr_0.8fr_0.8fr_0.9fr] gap-3 py-2 pr-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground ${selectMode ? "pl-12" : "pl-4"}`}>
            <span>Documento</span><span>Cliente</span><span>Data</span><span className="text-right">Valor</span><span className="text-right">Status</span>
          </div>
          {list.map((d) => (
            <button key={d.id} onClick={() => (selectMode ? toggle(d.id) : setSel(d))} data-testid={`doc-row-${d.id}`} aria-pressed={selectMode ? checked.includes(d.id) : undefined} className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors ${checked.includes(d.id) ? "bg-destructive/5" : ""}`}>
              {selectMode && <span className="flex items-center shrink-0" data-testid={`doc-check-${d.id}`}><Checkbox checked={checked.includes(d.id)} tabIndex={-1} className="pointer-events-none h-5 w-5" /></span>}
              <span className="flex-1 min-w-0 grid grid-cols-[1fr_auto] sm:grid-cols-[1.2fr_1.4fr_0.8fr_0.8fr_0.9fr] gap-x-3 gap-y-0.5 items-center">
                <span className="font-medium text-sm truncate">{DOC_TYPES[d.type]?.label} <span className="text-muted-foreground font-normal">{d.number}</span></span>
                <span className="text-sm truncate row-start-2 sm:row-start-auto col-span-2 sm:col-span-1 text-muted-foreground sm:text-foreground">{d.client_name}</span>
                <span className="text-sm text-muted-foreground hidden sm:block">{fmtDate(d)}</span>
                <span className="text-sm font-semibold text-right">{Number(d.total) > 0 ? formatBRL(d.total) : ""}</span>
                <span className="text-right hidden sm:block"><span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[d.status] || "text-muted-foreground"}`}>{d.status}</span></span>
                <span className="sm:hidden text-[11px] text-muted-foreground row-start-3 col-span-2">{fmtDate(d)} · {d.status}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-2" data-testid="docs-secondary-links">
        <Link to="/payments" className="hover:text-foreground">Pagamentos</Link><Link to="/recibos" className="hover:text-foreground">Recibos</Link><Link to="/reminders" className="hover:text-foreground">Lembretes de cobrança</Link>
      </div>

      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent className="max-w-sm" data-testid="docs-bulk-dialog">
          <DialogHeader><DialogTitle>Excluir {checked.length} documento(s)?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Essa ação não pode ser desfeita.</p>
          <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setConfirmBulk(false)}>Cancelar</Button><Button variant="destructive" onClick={bulkDelete} disabled={deleting} data-testid="docs-bulk-confirm">{deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-sm p-4" data-testid="doc-actions-dialog">
          {sel && <>
            <DialogHeader><DialogTitle className="text-base">{DOC_TYPES[sel.type]?.label} {sel.number}<span className="block text-sm font-normal text-muted-foreground">{sel.client_name}{Number(sel.total) > 0 ? ` · ${formatBRL(sel.total)}` : ""}</span></DialogTitle></DialogHeader>
            <DocActions doc={sel} onClose={() => setSel(null)} onChanged={load} />
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
